import { FloatingLabel, Form } from 'react-bootstrap';

interface EditDateProps {
  /** Floating label + placeholder. Omit to render a bare control (the caller supplies its own label, e.g. via `Field`). */
  readonly label?: string;
  /** A calendar day as `YYYY-MM-DD`, or `null` for empty. */
  readonly value: string | null;
  readonly onChange: (value: string | null) => void;
  readonly mandatory?: boolean;
  readonly disabled?: boolean;
  /** Earliest selectable day, as `YYYY-MM-DD`. Advisory — the server is the authority. */
  readonly min?: string;
  /** Latest selectable day, as `YYYY-MM-DD`. */
  readonly max?: string;
}

/**
 * Calendar-day input wrapper. Renders `<input type="date">` (browser native date
 * picker). Returns `null` for an empty value, otherwise the `YYYY-MM-DD` string
 * the picker emits — which is also how the wire carries a day, so no parsing or
 * formatting happens on either side.
 *
 * **A day, not an instant.** There is deliberately no timezone anywhere near this
 * control: `YYYY-MM-DD` is a calendar date, and converting it to a `Date` (or
 * through `moment`) to render it would attach the browser's zone and can shift the
 * day across a boundary. A separate `EditDateTime` will be needed for fields that
 * really are instants; it is not this component.
 *
 * When `label` is omitted the bare control renders and the caller owns the label.
 */
export const EditDate = ({ label, value, onChange, mandatory = false, disabled = false, min, max }: EditDateProps) => {
  const control = (
    <Form.Control
      type="date"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
      placeholder={label}
      required={mandatory}
      disabled={disabled}
      min={min}
      max={max}
    />
  );
  return label ? <FloatingLabel label={mandatory ? `${label} *` : label}>{control}</FloatingLabel> : control;
};
